/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from 'react';

import { useRecordContext, SelectField } from 'react-admin';

import { get, isArray, isEmpty } from 'lodash';

import {
  Box, Card, CardContent, Divider, Grid, Typography,
} from '@mui/material';

import { getName } from 'i18n-iso-countries';
import Answer from '../Answer';

import { countryConnectionChoices } from '../../../../constants/countryConnections';
import { CitizenshipParameters } from '../../questionParameters';
import applyChoices from '../../applyChoices';
import languages, { Languages } from '../../../../constants/languages';
import { countryChoices } from '../../../../constants/countries';
import conflictFields from '../../../../constants/conflictFields';

const TITLE = {
  [languages.SV]: 'Medborgarskap',
  [languages.EN]: 'Citizenship ',
  [languages.DE]: 'Staatsangehörigkeit',
};

const CONNECTION_FIELD_LABEL = {
  [languages.SV]: 'Anknytning',
  [languages.EN]: 'Connection',
  [languages.DE]: 'Beziehung',
};

const ValueField = ({ language, extraParameters }: {
  language?: Languages;
  extraParameters?: CitizenshipParameters;
}) => {
  const record = useRecordContext();

  if (!language) return null;

  const connectionChoices = applyChoices(
    countryConnectionChoices(language),
    extraParameters?.options,
    extraParameters?.choiceLabels,
  );

  const value = get(record, 'value') as {country: string; connections?: string[] }[] | undefined;
  const sx = get(record, 'sx', {});

  if (!isArray(value)) return null;

  return (
    <Card variant="outlined" sx={{ m: 0 }}>
      <Grid size={12}>
        <Grid container>
          {value.map((item, i) => (
            <Grid
              key={item.country}
              size={12}
            >
              <CardContent sx={{ p: 4 }}>
                <Grid container>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography variant="label">
                        {extraParameters?.fields?.countries?.label ?? TITLE[language]}
                      </Typography>
                      <SelectField
                        record={{ item }}
                        source="item.country"
                        choices={countryChoices(language)}
                        fontWeight="bold"
                        sx={sx}
                      />
                    </Box>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography variant="label">
                        {extraParameters?.fields?.connection?.label
                          ?? CONNECTION_FIELD_LABEL[language]}
                      </Typography>
                      {item.connections?.map((connection, j) => (
                        <Box display="flex" key={connection}>
                          <SelectField
                            record={{ connection }}
                            source="connection"
                            choices={connectionChoices}
                            fontWeight="bold"
                            sx={sx}
                          />
                          {(j + 1) !== item.connections?.length && (
                            <Typography mr={1}>,</Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
              {(i + 1) !== value.length && <Divider />}
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Card>
  );
};

const Citizenship = ({ extraParameters }: { extraParameters?: CitizenshipParameters }) => {
  const record = useRecordContext();

  const language = record?.language as Languages;

  return (
    <Answer
      label={extraParameters?.sections?.citizenship?.label ?? TITLE[language]}
      datapoint={conflictFields.CITIZENSHIP}
      identificationInfo={undefined}
      source="citizenship"
      isComplexType
    >
      <ValueField language={language} extraParameters={extraParameters} />
    </Answer>
  );
};

Citizenship.ValueField = ValueField;

Citizenship.Nested = ({ source, extraParameters }: {
  source?: string;
  extraParameters?: CitizenshipParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages;
  const connectionChoices = language
    ? applyChoices(
      countryConnectionChoices(language),
      extraParameters?.options,
      extraParameters?.choiceLabels,
    )
    : [];

  const countries = useMemo(
    () => get((record), `${source ?? 'citizenship'}`),
    [source, record],
  ) as {country: string; connections?: string[] }[] | undefined;

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="label">
            {extraParameters?.fields?.countries?.label ?? TITLE[language]}
          </Typography>
          <Box display="flex" gap={0}>
            <Typography>
              {
                countries?.map((item: {country: string; connection?: string }) => (
                  getName(item.country, language)
                )).join(', ')
              }
            </Typography>
            {isEmpty(countries) && <Typography>-</Typography>}
          </Box>
        </Box>
      </Grid>
      {countries?.map((item: {country: string; connections?: string[] }) => (
        <Grid
          key={item.country}
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="label">{`Connection to ${getName(item.country, language)}`}</Typography>
            <Typography>
              {item.connections?.map((connection) => (
                connectionChoices.find(({ id }) => id === connection)?.name
              )).join(', ')}
            </Typography>
            {isEmpty(item.connections) && <Typography>-</Typography>}
          </Box>
        </Grid>
      ))}
    </>
  );
};

export default Citizenship;
